import { Box, Typography, Autocomplete, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import ExcelUploadInput from '../../reuseable-components/ExcelInput/ExcelUploadInput';
import ExcelDownloadTemplateButton from '../../reuseable-components/ExcelInput/ExcelDownloadTemplateButton';
import SimpleAutocompleteProps from '../../reuseable-components/Autocomplete/SimpleAutocompleteProps';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import AGGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import axiosInstance from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';

ExcelProductUpload.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
}

export default function ExcelProductUpload({ open, setOpen }) {
  const gridRef = useRef(null);

  const [sheets, setSheets] = useState(null);
  const [headerRow, setHeaderRow] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [excelUploadData, setExcelUploadData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setExcelData([]);
    setHeaderRow([]);
    setExcelUploadData({});
    setSheets(null);
  }, [open]);

  useEffect(() => {
    if (headerRow?.length) {
      setExcelUploadData(data => ({
        ...data,
        descriptionCol: headerRow.find(c => c.name?.toLowerCase() === "description")?.value ?? data.descriptionCol,
        vendorCol: headerRow.find(c => c.name?.toLowerCase() === "vendor")?.value ?? data.vendorCol,
        vendorItemIdCol: headerRow.find(c => c.name?.toLowerCase() === "sku" || c.name?.toLowerCase() === "vendor item id")?.value ?? data.vendorItemIdCol,
        categoryCol: headerRow.find(c => c.name?.toLowerCase() === "category")?.value ?? data.categoryCol,
        uomTypeCol: headerRow.find(c => c.name?.toLowerCase() === "uom" || c.name?.toLowerCase() === "uom type")?.value ?? data.uomTypeCol,
        uomAmountCol: headerRow.find(c => c.name?.toLowerCase() === "uom amount")?.value ?? data.uomAmountCol,
        manufacturerCol: headerRow.find(c => c.name?.toLowerCase() === "manufacturer")?.value ?? data.manufacturerCol,
        manufacturerIdCol: headerRow.find(c => c.name?.toLowerCase() === "manufacturer id" || c.name?.toLowerCase() === "manufacturer sku")?.value ?? data.manufacturerIdCol,
        manufacturerLinkCol: headerRow.find(c => c.name?.toLowerCase() === "manufacturer link")?.value ?? data.manufacturerLinkCol,
        priceCol: headerRow.find(c => c.name?.toLowerCase() === "price" || c.name?.toLowerCase() === "cost")?.value ?? data.priceCol,
        pictureUrlCol: headerRow.find(c => c.name?.toLowerCase() === "picture" || c.name?.toLowerCase() === "picture url")?.value ?? data.pictureUrlCol,
      }));
    }
  }, [headerRow]);

  const submit = () => {
    setSubmitting(true);
    axiosInstance.post("/Product/ExcelBulkProductUpload", objectToFormData({
      sheet: excelUploadData.sheet,
      file: excelUploadData.file,
      fields: [
        { field: "Description", col: excelUploadData.descriptionCol },
        { field: "Vendor", col: excelUploadData.vendorCol },
        { field: "VendorItemId", col: excelUploadData.vendorItemIdCol },
        { field: "Category", col: excelUploadData.categoryCol }, 
        { field: "UOMType", col: excelUploadData.uomTypeCol },
        { field: "UOMAmount", col: excelUploadData.uomAmountCol },
        { field: "Price", col: excelUploadData.priceCol },
        { field: "Manufacturer", col: excelUploadData.manufacturerCol },
        { field: "ManufacturerId", col: excelUploadData.manufacturerIdCol },
        { field: "ManufacturerLink", col: excelUploadData.manufacturerLinkCol },
        { field: "PictureUrl", col: excelUploadData.pictureUrlCol },
      ]
    }))
      .then(() => {
        enqueueSnackbar("Product upload started. You will receive an email when processing is complete.", { variant: "success" });
        setOpen(false);
      })
      .catch(() => enqueueSnackbar("Product upload failed", { variant: "error" }))
      .finally(() => setSubmitting(false));
  };

  const columns = useMemo( () => headerRow.map(c => ({ field: c.value, headerName: c.label, flex: 1 })), [headerRow]);
  const rows = useMemo( () => (excelData.length > 0 ? excelData.slice(1) : excelData), [excelData]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ textAlign: 'center' }}>
        Product Excel Upload
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center">
              <ExcelUploadInput
                setSheets={setSheets}
                setFile={(file) => setExcelUploadData(d => ({ ...(d ?? {}), file }))}
                setSelectedSheet={(sheet) => setExcelUploadData(d => ({ ...(d ?? {}), sheet }))}
                setHeaderRow={setHeaderRow}
                setExcelData={setExcelData}
                selectedSheet={excelUploadData?.sheet}
                ignoreEmptyRows
              />
              <ExcelDownloadTemplateButton fields={["Description", "Vendor", "SKU", "Category", "UOM Type", "UOM Amount", "Price", "Manufacturer", "Manufacturer SKU", "Manufacturer Link", "Picture URL"]}
                fileName="Product Upload Template.xlsx" worksheetName="Products" />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography>Sheet</Typography>
            <Autocomplete
              fullWidth
              value={ excelUploadData?.sheet ? { value: excelUploadData.sheet, label: excelUploadData.sheet } : null}
              onChange={(e, v) => setExcelUploadData(d => ({ ...(d ?? {}), sheet: v?.value }))}
              options={sheets?.map(s => ({ value: s, label: s })) ?? []}
              {...SimpleAutocompleteProps}
            />
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography>Description</Typography>
              <Autocomplete
                value={ excelUploadData?.descriptionCol ? headerRow.find(c => c.value === excelUploadData.descriptionCol) : null}
                onChange={(e, v) => setExcelUploadData(d => ({ ...(d ?? {}), descriptionCol: v?.value }))}
                options={headerRow}
                {...SimpleAutocompleteProps}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography>Vendor</Typography>
              <Autocomplete
                value={ excelUploadData?.vendorCol ? headerRow.find(c => c.value === excelUploadData.vendorCol) : null }
                onChange={(e, v) => setExcelUploadData(d => ({ ...(d ?? {}), vendorCol: v?.value }))}
                options={headerRow}
                {...SimpleAutocompleteProps}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography>Vendor Item Id</Typography>
              <Autocomplete
                value={ excelUploadData?.vendorItemIdCol ? headerRow.find(c => c.value === excelUploadData.vendorItemIdCol) : null}
                onChange={(e, v) => setExcelUploadData(d => ({ ...(d ?? {}), vendorItemIdCol: v?.value }))}
                options={headerRow}
                {...SimpleAutocompleteProps}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography>Category</Typography>
              <Autocomplete
                value={excelUploadData?.categoryCol ? headerRow.find(c => c.value === excelUploadData.categoryCol) : null}
                onChange={(e, v) => setExcelUploadData(d => ({ ...(d ?? {}), categoryCol: v?.value }))}
                options={headerRow}
                {...SimpleAutocompleteProps}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography>UOM Type</Typography>
              <Autocomplete
                value={ excelUploadData?.uomTypeCol ? headerRow.find(c => c.value === excelUploadData.uomTypeCol) : null}
                onChange={(e, v) => setExcelUploadData(d => ({ ...(d ?? {}), uomTypeCol: v?.value }))}
                options={headerRow}
                {...SimpleAutocompleteProps}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography>UOM Amount</Typography>
              <Autocomplete
                value={ excelUploadData?.uomAmountCol ? headerRow.find(c => c.value === excelUploadData.uomAmountCol) : null}
                onChange={(e, v) => setExcelUploadData(d => ({ ...(d ?? {}), uomAmountCol: v?.value }))}
                options={headerRow}
                {...SimpleAutocompleteProps}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography>Price</Typography>
              <Autocomplete
                value={ excelUploadData?.priceCol ? headerRow.find(c => c.value === excelUploadData.priceCol) : null}
                onChange={(e, v) => setExcelUploadData(d => ({ ...(d ?? {}), priceCol: v?.value }))}
                options={headerRow}
                {...SimpleAutocompleteProps}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography>Manufacturer</Typography>
              <Autocomplete
                value={excelUploadData?.manufacturerCol ? headerRow.find(c => c.value === excelUploadData.manufacturerCol) : null}
                onChange={(e, v) => setExcelUploadData(d => ({ ...(d ?? {}), manufacturerCol: v?.value }))}
                options={headerRow}
                {...SimpleAutocompleteProps}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography>Manufacturer SKU</Typography>
              <Autocomplete
                value={excelUploadData?.manufacturerCol ? headerRow.find(c => c.value === excelUploadData.manufacturerIdCol) : null}
                onChange={(e, v) => setExcelUploadData(d => ({ ...(d ?? {}), manufacturerIdCol: v?.value }))}
                options={headerRow}
                {...SimpleAutocompleteProps}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography>Manufacturer Link</Typography>
              <Autocomplete
                value={excelUploadData?.manufacturerCol ? headerRow.find(c => c.value === excelUploadData.manufacturerLinkCol) : null}
                onChange={(e, v) => setExcelUploadData(d => ({ ...(d ?? {}), manufacturerLinkCol: v?.value }))}
                options={headerRow}
                {...SimpleAutocompleteProps}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography>Picture URL</Typography>
              <Autocomplete
                value={excelUploadData?.pictureUrlCol ? headerRow.find(c => c.value === excelUploadData.pictureUrlCol) : null}
                onChange={(e, v) => setExcelUploadData(d => ({ ...(d ?? {}), pictureUrlCol: v?.value }))}
                options={headerRow}
                {...SimpleAutocompleteProps}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ height: 300 }}>
              <AGGridQuickFilterWrapper gridRef={gridRef} />
              <Box sx={{ height: 270 }}>
                <AgGridReact ref={gridRef} {...AGGridDefaults} columnDefs={columns} rowData={rows}/>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" sx={{ width: 150 }} onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          sx={{ width: 150 }}
          loading={submitting}
          disabled={ !excelUploadData?.file}
          onClick={submit}
        >
          Upload
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}